import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';

import useDispatch from 'client/hooks/useDispatch';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import { formatPrice } from 'common/formatters';
import {
  LISTING_CMA_DISCUSSION_ACKNOWLEDGED,
  LISTING_CMA_DISCUSSION_AGREED,
  LISTING_CMA_DISCUSSION_NOT_DISCUSSED,
  LISTING_CMA_DISCUSSION_VALUES,
} from 'common/listingConstants';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import SrBox from 'client/components/SrBox';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_HEIGHT_TALL,
  SR_BUTTON_FONT_SIZE_SMALL,
} from 'client/components/SrButton/SrButton';
import SrCMADiscussionModal from 'client/components/SrCMADiscussionModal/SrCMADiscussionModal';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';

import { actionDataTableLoadItems } from 'client/helpers/dataTables';
import { DATA_TABLE_SOURCE_PARTNER_LISTINGS } from 'common/dataTableConstants';

import './SrPriceComparisonCard.less';

export const SR_PRICE_COMPARISON_CARD_COLOR_GREEN = 'SR_PRICE_COMPARISON_CARD_COLOR_GREEN';
export const SR_PRICE_COMPARISON_CARD_COLOR_ORANGE = 'SR_PRICE_COMPARISON_CARD_COLOR_ORANGE';

const SR_PRICE_COMPARISON_CARD_COLOR_CLASSNAME_MAPPING = {
  [SR_PRICE_COMPARISON_CARD_COLOR_GREEN]: 'colorTypeGreen',
  [SR_PRICE_COMPARISON_CARD_COLOR_ORANGE]: 'colorTypeOrange',
};

const SR_PRICE_COMPARISON_CARD_COLORS = Object.keys(SR_PRICE_COMPARISON_CARD_COLOR_CLASSNAME_MAPPING);
const LISTING_CMA_PRICE_IS_IN_RANGE = 'LISTING_PRICE_IS_IN_RANGE';

const SR_PRICE_DISCUSSION_HEADER = {
  [LISTING_CMA_DISCUSSION_NOT_DISCUSSED]: {
    text: 'Pricing has not been discussed with partner',
    color: 'red',
    icon:'/images/x-circle-icon-red.svg',
  },
  [LISTING_CMA_DISCUSSION_ACKNOWLEDGED]: {
    text: 'The partner acknowledged it’s overpriced and not willing to lower price',
    color: 'orange300',
    icon: '/images/checkmark-circle-orange.svg',
  },
  [LISTING_CMA_DISCUSSION_AGREED]: {
    text: 'The partner has agreed to lower the price',
    color: 'green',
    icon:'/images/checkmark-circle-green-no-shadow.svg',
  },
  [LISTING_CMA_PRICE_IS_IN_RANGE]:{
    text: 'Price is already near or matching the CMA price',
    color: 'green',
    icon:'/images/checkmark-circle-green-no-shadow.svg',
  },
};

const SrPriceComparisonCard = ({
  buttonText,
  colorType,
  descriptionTitle,
  descriptionText,
  onButtonClick,
  priceLeft,
  priceRight,
  priceLeftLabel,
  priceRightLabel,
  ctaText,
  onCtaClick,
  isAdmin,
  isRentPriceTooHigh,
  listingId,
  cmaDiscussed,
  cmaDiscussionNotes,
  cmaDiscussedAt,
  cmaDiscussedBy,
}) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const dispatch = useDispatch();
  const [modalActive, setModalActive] = useState(false);

  const getDiscussionNotesText = () => {
    if (!cmaDiscussionNotes) {
      return '';
    }

    const dateText = momentWithAustinTimezone(cmaDiscussedAt).format('MMM DD HH:mm:ss');

    return `${cmaDiscussionNotes} - ${cmaDiscussedBy}, ${dateText}`;
  };

  const openDiscussionModal = () => {
    setModalActive(true);
  };

  const closeDiscussionModal = () => {
    setModalActive(false);
  };

  const reloadDataTableListings = () => {
    dispatch(actionDataTableLoadItems, {
      itemsPropName: 'listings',
      dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
      viewId: `by-listingId-${listingId}`,
      count: 1,
      filter: {
        'listings.id': listingId,
      },
    });
  };

  const hasDescription = descriptionTitle && descriptionText;
  const shouldDisplayCmaDiscussionNotes = isRentPriceTooHigh
    && cmaDiscussed === LISTING_CMA_DISCUSSION_ACKNOWLEDGED
    && cmaDiscussionNotes;

  const headerType = isRentPriceTooHigh
    ? cmaDiscussed
    : LISTING_CMA_PRICE_IS_IN_RANGE;

  const {
    text: cmaDiscussionText,
    color: cmaDiscussionColor,
    icon: cmaDiscussionIcon,
  } = SR_PRICE_DISCUSSION_HEADER[headerType];

  const cmaDiscussionNotesText = getDiscussionNotesText();

  return (
    <>
      <SrBox
        boxSizing='border-box'
        borderRadius='14px'
        styleName={SR_PRICE_COMPARISON_CARD_COLOR_CLASSNAME_MAPPING[colorType]}
      >
        <SrFlex height='91px' styleName='pricesContainer'>
          <SrBox
            width='50%'
            styleName='priceLeftContainer'
            paddingTop='18px'
          >
            <SrText
              styleName='priceLeft'
              display='block'
              font='greycliff'
              weight='800'
              size='28'
              align='center'
              lineHeight='34px'
            >
              {formatPrice(priceLeft)}
            </SrText>
            <SrText
              styleName='priceLeftLabel'
              display='block'
              align='center'
              size='13'
              lineHeight='16px'
              weight='800'
              font='greycliff'
            >
              {priceLeftLabel}
            </SrText>
          </SrBox>
          <SrBox
            width='50%'
            paddingTop='18px'
          >
            <SrText
              display='block'
              font='greycliff'
              weight='800'
              size='28'
              align='center'
              lineHeight='34px'
              color='blue300'
            >
              {formatPrice(priceRight)}
            </SrText>
            <SrText
              display='block'
              align='center'
              size='13'
              lineHeight='16px'
              weight='800'
              font='greycliff'
              color='blue300'
            >
              {priceRightLabel}
            </SrText>
          </SrBox>
        </SrFlex>
        {isAdmin && (
          <SrFlex
            styleName={
              classNames('cmaDiscussionContainer',
                SR_PRICE_COMPARISON_CARD_COLOR_CLASSNAME_MAPPING[colorType]
              )
            }
          >
            <SrFlex
              align='center'
              justify='center'
              paddingLeft='10px'
              paddingRight='10px'
            >
              <SrBox
                width='20px'
                height='20px'
                backgroundImage={`url(${cmaDiscussionIcon})`}
                marginRight='8px'
              />
              <SrBox styleName='cmaDiscussionText'>
                <SrText
                  font='greycliff'
                  weight='800'
                  color={cmaDiscussionColor}
                >
                  {cmaDiscussionText}
                </SrText>
              </SrBox>
            </SrFlex>
            {shouldDisplayCmaDiscussionNotes && (
              <SrFlex
                align='center'
                justify='center'
                marginTop='16px'
                paddingLeft='10px'
                paddingRight='10px'
              >
                <SrBox
                  maxWidth='556px'
                  textAlign='center'
                >
                  <SrText
                    font='greycliff'
                    color='grey'
                    weight='600'
                    size='18px'
                    lineHeight='24px'
                  >
                    {cmaDiscussionNotesText}
                  </SrText>
                </SrBox>
              </SrFlex>
            )}
            {isRentPriceTooHigh && (
              <SrFlex
                marginTop='14px'
                align='center'
                justify='center'
                cursor='pointer'
                onClick={openDiscussionModal}
              >
                <SrText
                  font='greycliff'
                  weight='800'
                  color='blue300'
                >
                  Update pricing conversation &gt;
                </SrText>
              </SrFlex>
            )}
          </SrFlex>
        )}
        {hasDescription && (
          <SrBox padding={isSmallScreen ? '13px 14px 0' : '20px 14px 0'}>
            <SrText
              display='block'
              font='greycliff'
              color='blueGreycliff'
              weight='800'
              size='16'
              lineHeight='25px'
              align={!isSmallScreen ? 'center' : null}
            >
              {descriptionTitle}
            </SrText>
            <SrBox maxWidth={descriptionText.length < 255 ? '550px' : '850px'} margin='7px auto 0'>
              <SrText
                display='block'
                font='greycliff'
                color='blueGreycliff'
                opacity='0.6'
                weight='600'
                size={isSmallScreen ? '16' : '18'}
                lineHeight='24px'
                align={!isSmallScreen ? 'center' : null}
              >
                {descriptionText}
              </SrText>
            </SrBox>
          </SrBox>
        )}
        <SrBox
          onClick={onButtonClick}
          padding={hasDescription ? '17px 0 27px' : '24px 0 27px'}
          cursor='pointer'
        >
          <SrText
            font='greycliff'
            weight='800'
            color='blue300'
            size='16'
            lineHeight='19px'
            align='center'
            display='block'
          >
            {buttonText}
          </SrText>
        </SrBox>
        {ctaText && onCtaClick && (
          <SrBox margin='2px auto 21px' maxWidth='303px'>
            <SrButton
              theme={SR_BUTTON_THEME_ADOBE_BLUE}
              heightType={SR_BUTTON_HEIGHT_TALL}
              fontSizeType={SR_BUTTON_FONT_SIZE_SMALL}
              onClick={onCtaClick}
            >
              {ctaText}
            </SrButton>
          </SrBox>
        )}
      </SrBox>
      {modalActive && (
        <SrCMADiscussionModal
          listingId={listingId}
          onClose={closeDiscussionModal}
          onSuccess={reloadDataTableListings}
          cmaDiscussed={cmaDiscussed}
          cmaDiscussionNotes={cmaDiscussionNotes}
        />
      )}
    </>
  );
};

SrPriceComparisonCard.propTypes = {
  buttonText: PropTypes.string.isRequired,
  colorType: PropTypes.oneOf(SR_PRICE_COMPARISON_CARD_COLORS),
  descriptionTitle: PropTypes.string,
  descriptionText: PropTypes.string,
  priceLeft: PropTypes.number.isRequired,
  priceRight: PropTypes.number.isRequired,
  priceLeftLabel: PropTypes.string.isRequired,
  priceRightLabel: PropTypes.string.isRequired,
  onButtonClick: PropTypes.func.isRequired,
  ctaText: PropTypes.string,
  onCtaClick: PropTypes.func,
  isAdmin: PropTypes.bool.isRequired,
  isRentPriceTooHigh: PropTypes.bool.isRequired,
  listingId: PropTypes.number.isRequired,
  cmaDiscussed: PropTypes.oneOf(LISTING_CMA_DISCUSSION_VALUES),
};

SrPriceComparisonCard.defaultProps = {
  colorType: SR_PRICE_COMPARISON_CARD_COLOR_GREEN,
};

SrPriceComparisonCard.displayName = 'SrPriceComparisonCard';

export default SrPriceComparisonCard;
